import React, { useEffect, useRef } from 'react';
import Button from '@material-ui/core/Button';
import { useSelector } from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import DialogTitle from '@material-ui/core/DialogTitle';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import FormLabel from '@material-ui/core/FormLabel';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next';
import { editedTicketState } from '../EditTicket/EditTicketSlice';
import useStyles from './styles';
import i18n from '../../i18n';
import ApiService from '../../services/ApiService';
import FileUploader from '../AddAnomaly/FileUploader/FileUploader';

function EmailPopUp(props) {
  const { open, ticketNumber, triggerEmailPopUp } = props;
  const [ccErrorMessage, setCcErrorMessage] = React.useState(null);
  const [toErrorMessage, setToErrorMessage] = React.useState(null);
  const [objectErrorMessage, setObjectErrorMessage] = React.useState(null);
  const [bodyErrorMessage, setBodyErrorMessage] = React.useState(null);
  const [object, setObject] = React.useState('');
  const [body, setBody] = React.useState('');
  const [toEmailAddresses, setToEmailAddresses] = React.useState('');
  const [ccEmailAddresses, setCcEmailAddresses] = React.useState('');
  const [attachments, setAttachments] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [sendClicked, setSendClicked] = React.useState(false);
  const [initialFiles, setInitialFiles] = React.useState([]);

  const [newUploadedAttachments, setNewUploadedAttachments] = React.useState(
    [],
  );

  const classes = useStyles();
  const mountedRef = useRef(true);
  const { t } = useTranslation();
  const lang = i18n.language;
  const editedTicket = useSelector(editedTicketState);

  function readEmailAddressesFromList(emailList) {
    let emails = '';
    if (emailList !== undefined && emailList.length > 0) {
      emailList.forEach((email) => {
        emails = emails.concat(email).concat(';');
      });
    }
    return emails;
  }

  async function getEmailTemplate() {
    const receivedEmailTemplate = await ApiService.getFormattedMailTemplate(
      ticketNumber,
    );
    setObject(receivedEmailTemplate.data.object);
    setBody(receivedEmailTemplate.data.body);
    setToEmailAddresses(
      readEmailAddressesFromList(receivedEmailTemplate.data.toEmailAddresses),
    );
    setCcEmailAddresses(
      readEmailAddressesFromList(receivedEmailTemplate.data.ccEmailAddresses),
    );
    setAttachments(receivedEmailTemplate.data.attachments);
    setInitialFiles(
      receivedEmailTemplate && receivedEmailTemplate.data
        ? receivedEmailTemplate.data.attachments
        : [],
    );
  }

  function setEmailErrorsInSelectedLanguage(message, language) {
    let errorMessageWithRightLang = {};
    if (message) {
      if (language === 'en') {
        errorMessageWithRightLang = message.errorsEN;
      } else if (language === 'fr') {
        errorMessageWithRightLang = message.errorsFR;
      } else if (language === 'de') {
        errorMessageWithRightLang = message.errorsDE;
      } else {
        errorMessageWithRightLang = message.errorsEN;
      }
    }
    if (errorMessageWithRightLang.cc !== undefined) {
      setCcErrorMessage(errorMessageWithRightLang.cc);
    }
    if (errorMessageWithRightLang.to !== undefined) {
      setToErrorMessage(errorMessageWithRightLang.to);
    }
    if (errorMessageWithRightLang.object !== undefined) {
      setObjectErrorMessage(errorMessageWithRightLang.object);
    }
    if (errorMessageWithRightLang.body !== undefined) {
      setBodyErrorMessage(errorMessageWithRightLang.body);
    }
  }

  useEffect(() => {
    getEmailTemplate();
  }, []);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleClose = () => {
    props.setEmailPopUpOpenedState(false);
  };

  function getAttachments(attachmentsSelected) {
    setAttachments(attachmentsSelected.slice());
  }

  function getAttachmentsWithFile(files) {
    setNewUploadedAttachments(files);
  }

  function splitEmailsToArray(emails) {
    const emailsArray = emails.indexOf(';')
      ? emails.split(';')
      : new Array(emails);
    return emailsArray.filter((email) => email.replaceAll(/\s/g, '') !== '');
  }

  function createEmailFormData() {
    const data = new FormData();
    data.append('ticketNumber', ticketNumber);
    data.append(
      'mail',
      new Blob(
        [
          JSON.stringify({
            toEmailAddresses: splitEmailsToArray(toEmailAddresses),
            ccEmailAddresses: splitEmailsToArray(ccEmailAddresses),
            object,
            body,
            attachments: attachments || [],
          }),
        ],
        {
          type: 'application/json',
        },
      ),
    );
    newUploadedAttachments.map((file) => {
      data.append('files', file);
      return data;
    });
    return data;
  }

  function handleValidate() {
    if (mountedRef.current) {
      setSendClicked(true);
      setLoading(true);
      setTimeout(() => {
        const emailFormData = createEmailFormData();
        ApiService.sendEmail(emailFormData)
          .then(() => {
            setLoading(false);
            props.setEmailPopUpOpenedState(false);
          })
          .catch((error) => {
            setLoading(false);
            if (error) {
              setEmailErrorsInSelectedLanguage(error, lang);
            }
          });
      }, 300);
    }
  }

  function generateDialogTitleText() {
    let titleText = `${t('Ticket')} ${editedTicket.ticketNumber} `;
    if (triggerEmailPopUp === 'created') {
      titleText = titleText.concat(t('created'));
    } else if (triggerEmailPopUp === 'edit') {
      titleText = titleText.concat(t('changed'));
    }
    return titleText;
  }
  return (
    <Dialog
      open={open}
      scroll="body"
      disableBackdropClick
      onClose={handleClose}
      classes={{ paper: classes.paper }}
    >
      {loading && (
        <Paper
          elevation={0}
          position="absolute"
          zindex="2"
          className={classes.loadingPaper}
        >
          <CircularProgress className={classes.loader} />
        </Paper>
      )}
      <Grid container spacing={0}>
        <Grid item xs={1}>
          <EmailOutlinedIcon className={classes.EmailIcon} fontSize="large" />
        </Grid>
        <Grid item xs={10}>
          <DialogTitle className={classes.dialogTitle}>
            <Box display="flex" alignItems="center">
              <Box flexGrow={1}>
                <Typography className={classes.title}>
                  {generateDialogTitleText()}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent className={classes.dialog}>
            <DialogContentText className={classes.dialogText}>
              {t('Email introduction')}
            </DialogContentText>
            <DialogContentText className={classes.subTitle}>
              {t('Mail Details')}
            </DialogContentText>
            <FormLabel className={classes.formLabel}>{t('to')}</FormLabel>
            <TextField
              className={classes.input}
              InputProps={{ classes: { input: classes.inputStyle } }}
              required
              size="small"
              value={toEmailAddresses}
              onChange={(e) => {
                setToEmailAddresses(e.target.value);
                setToErrorMessage(null);
              }}
              variant="outlined"
              error={toErrorMessage !== null}
              helperText={toErrorMessage}
            />
            <FormLabel className={classes.formLabel}>{t('cc')}</FormLabel>
            <TextField
              className={classes.input}
              InputProps={{ classes: { input: classes.inputStyle } }}
              size="small"
              value={ccEmailAddresses}
              onChange={(e) => {
                setCcEmailAddresses(e.target.value);
                setCcErrorMessage(null);
              }}
              variant="outlined"
              error={ccErrorMessage !== null}
              helperText={ccErrorMessage}
            />
            <FormLabel className={classes.formLabel}>{t('object')} </FormLabel>
            <TextField
              className={classes.input}
              InputProps={{ classes: { input: classes.inputStyle } }}
              required
              value={object}
              onChange={(e) => {
                setObject(e.target.value);
                setObjectErrorMessage(null);
              }}
              size="small"
              variant="outlined"
              error={objectErrorMessage !== null}
              helperText={objectErrorMessage}
            />
            <FormLabel className={classes.formLabel}>{t('body')} </FormLabel>
            <TextField
              className={classes.input}
              InputProps={{ classes: { input: classes.inputStyle } }}
              required
              multiline
              rows={5}
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                setBodyErrorMessage(null);
              }}
              variant="outlined"
              error={bodyErrorMessage !== null}
              helperText={bodyErrorMessage}
            />
            <FileUploader
              sendAttachmentWithFiles={getAttachmentsWithFile}
              isEmailPopUp
              files={initialFiles ? initialFiles.slice() : []}
              sendAttachments={getAttachments}
              saveClicked={sendClicked}
            />
          </DialogContent>
          <DialogActions>
            <Button
              className={classes.validateButton}
              variant="contained"
              autoFocus
              onClick={handleValidate}
              color="secondary"
            >
              {t('Validate')}
            </Button>
          </DialogActions>
        </Grid>
        <Grid item xs={1}>
          <IconButton className={classes.closeIcon} onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>
    </Dialog>
  );
}

export default EmailPopUp;
